import type {
  AutomationClassDto,
  AutomationClassNotesDto,
  AutomationCopiedProjectDto,
  AutomationDataChangeDto,
  AutomationDeletionImpactDto,
  AutomationFullRunDto,
  AutomationFullRunSummaryDto,
  AutomationKnownDefectDto,
  AutomationProjectDto,
  AutomationRunCausesDto,
  AutomationRunConfig,
  AutomationRunDto,
  AutomationRunResultsDto,
  AutomationScanAppliedDto,
  AutomationScanPreviewDto,
  AutomationStartedFullRunDto,
  AutomationStartedRunDto,
  AutomationSuiteDto,
  AutomationTeamDto,
  AutomationTestDatumDto,
  AutomationTestEmployeesDto,
  AutomationTestPlanDto,
} from '@tms/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';
import { useAutomationAccess } from './api';
import { automationRequest } from './client';
import type {
  AccountChanges,
  PoolAccount,
  PoolAccountSummary,
  PoolList,
  ReadinessMatrix,
  RegistrationList,
} from './testrun-types';

/**
 * The data of the automation pages. TMS keeps the registry and the runs
 * (`/api/v1/automation/*`); TestRun keeps the test accounts and is called from
 * the browser with a TMS token (`automationRequest`). Page-specific TestRun
 * calls live next to their pages; this file has what several pages share.
 */

const A = '/automation';
const enc = encodeURIComponent;
/** A run id as TestRun showed it (`#123`) or a bare pipeline id → the pipeline id. */
export const pipelineIdOf = (runId: string): string => runId.replace('#', '');

/** While something runs, pages re-read it this often; the API asks GitLab on every call. */
export const RUN_POLL_MS = 5_000;
const LIST_POLL_MS = 15_000;

/* ─── roles ─── */

/**
 * What the signed-in user may do here, by the TestRun role their TMS role maps
 * to: Viewer reads, QA runs and edits test data, Admin manages the registry.
 */
export const useAutomationCan = () => {
  const { data } = useAutomationAccess();
  const role = data?.role;
  return {
    role,
    run: role === 'QA' || role === 'Admin',
    manage: role === 'Admin',
  };
};

/* ─── teams and projects ─── */

export const useAutomationTeam = (teamId: string) =>
  useQuery({
    queryKey: queryKeys.automation.team(teamId),
    queryFn: ({ signal }) =>
      api.get<AutomationTeamDto>(`${A}/teams/${enc(teamId)}`, undefined, signal),
  });

export const useAutomationProject = (projectId: string) =>
  useQuery({
    queryKey: queryKeys.automation.project(projectId),
    queryFn: ({ signal }) =>
      api.get<AutomationProjectDto>(`${A}/projects/${enc(projectId)}`, undefined, signal),
  });

/** After a registry change the team cards, team pages and project headers are all stale. */
const invalidateRegistry = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: queryKeys.automation.all });

export interface TeamInput {
  name: string;
  description?: string;
  memberNames?: string[];
  projects?: ProjectInput[];
}

export interface ProjectInput {
  name: string;
  description?: string;
  note?: string;
  gitlabProjectId?: string;
  gitlabPipelineUrl?: string;
  jenkinsUrl?: string | null;
  testType?: 'API' | 'UI';
  repoPath?: string | null;
  /** write-only; '' removes it, absent keeps it */
  triggerToken?: string | null;
}

export const useCreateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TeamInput) => api.post<AutomationTeamDto>(`${A}/teams`, input),
    onSuccess: () => invalidateRegistry(queryClient),
  });
};

export const useUpdateTeam = (teamId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Omit<TeamInput, 'projects'>>) =>
      api.patch<AutomationTeamDto>(`${A}/teams/${enc(teamId)}`, input),
    onSuccess: () => invalidateRegistry(queryClient),
  });
};

export const useTeamDeletionImpact = (teamId: string, enabled: boolean) =>
  useQuery({
    queryKey: [...queryKeys.automation.team(teamId), 'deletion-impact'],
    queryFn: ({ signal }) =>
      api.get<AutomationDeletionImpactDto>(
        `${A}/teams/${enc(teamId)}/deletion-impact`,
        undefined,
        signal,
      ),
    enabled,
  });

export const useDeleteTeam = (teamId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.delete<{ ok: true; deletedProjects: number }>(`${A}/teams/${enc(teamId)}`),
    onSuccess: () => invalidateRegistry(queryClient),
  });
};

export const useCreateProject = (teamId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectInput) =>
      api.post<AutomationProjectDto>(`${A}/teams/${enc(teamId)}/projects`, input),
    onSuccess: () => invalidateRegistry(queryClient),
  });
};

export const useCopyProject = (teamId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sourceProjectId: string; name?: string; withTestData?: boolean }) =>
      api.post<AutomationCopiedProjectDto>(`${A}/teams/${enc(teamId)}/projects/copy`, input),
    onSuccess: () => invalidateRegistry(queryClient),
  });
};

export const useUpdateProject = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ProjectInput>) =>
      api.patch<AutomationProjectDto>(`${A}/projects/${enc(projectId)}`, input),
    onSuccess: () => invalidateRegistry(queryClient),
  });
};

export const useDeleteProject = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ ok: true }>(`${A}/projects/${enc(projectId)}`),
    onSuccess: () => invalidateRegistry(queryClient),
  });
};

/* ─── the catalogue ─── */

const projectPart = <T>(projectId: string, part: string, path: string, enabled = true) =>
  ({
    queryKey: queryKeys.automation.projectData(projectId, part),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.get<T>(`${A}/projects/${enc(projectId)}${path}`, undefined, signal),
    enabled: enabled && Boolean(projectId),
  }) as const;

export const useSuites = (projectId: string) =>
  useQuery(projectPart<AutomationSuiteDto[]>(projectId, 'suites', '/suites'));

export const useClasses = (projectId: string) =>
  useQuery(projectPart<AutomationClassDto[]>(projectId, 'classes', '/classes'));

/** The scanned plan; null when the repository was never scanned. */
export const useTestPlan = (projectId: string, enabled = true) =>
  useQuery(projectPart<AutomationTestPlanDto | null>(projectId, 'plan', '/plan', enabled));

export const useClassNotes = (projectId: string, enabled = true) =>
  useQuery(projectPart<AutomationClassNotesDto>(projectId, 'class-notes', '/class-notes', enabled));

export const useTestData = (projectId: string) =>
  useQuery(projectPart<AutomationTestDatumDto[]>(projectId, 'testdata', '/testdata'));

export const useDataChanges = (projectId: string) =>
  useQuery(
    projectPart<AutomationDataChangeDto[]>(projectId, 'testdata-changes', '/testdata/changes'),
  );

const invalidateProject = (queryClient: QueryClient, projectId: string) =>
  queryClient.invalidateQueries({ queryKey: queryKeys.automation.project(projectId) });

export const useCreateSuite = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string }) =>
      api.post<AutomationSuiteDto>(`${A}/projects/${enc(projectId)}/suites`, input),
    onSuccess: () => invalidateProject(queryClient, projectId),
  });
};

export const useDeleteSuite = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (suiteId: string) => api.delete<{ ok: true }>(`${A}/suites/${enc(suiteId)}`),
    onSuccess: () => invalidateProject(queryClient, projectId),
  });
};

export const useSetClassNote = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ className, note }: { className: string; note: string }) =>
      api.put<{ ok: true; className: string }>(
        `${A}/projects/${enc(projectId)}/class-notes/${enc(className)}`,
        { note },
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.automation.projectData(projectId, 'class-notes'),
      }),
  });
};

export const useUpsertDatum = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id?: string; key: string; value: string }) =>
      api.put<AutomationTestDatumDto>(`${A}/projects/${enc(projectId)}/testdata`, input),
    onSuccess: () => invalidateProject(queryClient, projectId),
  });
};

export const useDeleteDatum = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: true }>(`${A}/projects/${enc(projectId)}/testdata/${enc(id)}`),
    onSuccess: () => invalidateProject(queryClient, projectId),
  });
};

export const useImportTestData = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      api.post<{ imported: number }>(`${A}/projects/${enc(projectId)}/testdata/import`, { text }),
    onSuccess: () => invalidateProject(queryClient, projectId),
  });
};

/** A scan clones the repository and diffs it with the catalogue; applying is a second step. */
export const useScanRepo = (projectId: string) =>
  useMutation({
    mutationFn: () => api.post<AutomationScanPreviewDto>(`${A}/projects/${enc(projectId)}/scan`),
  });

export const useApplyScan = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (parts: { suites: boolean; classes: boolean; data: boolean }) =>
      api.post<AutomationScanAppliedDto>(`${A}/projects/${enc(projectId)}/scan/apply`, parts),
    onSuccess: () => invalidateRegistry(queryClient),
  });
};

/* ─── runs ─── */

export const useProjectRuns = (projectId: string) =>
  useQuery({
    queryKey: queryKeys.automation.projectData(projectId, 'runs'),
    queryFn: ({ signal }) =>
      api.get<AutomationRunDto[]>(`${A}/projects/${enc(projectId)}/runs`, undefined, signal),
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === 'running' || run.status === 'pending')
        ? RUN_POLL_MS
        : LIST_POLL_MS,
  });

export const useTeamRuns = (teamId: string) =>
  useQuery({
    queryKey: queryKeys.automation.teamRuns(teamId),
    queryFn: ({ signal }) =>
      api.get<AutomationRunDto[]>(`${A}/teams/${enc(teamId)}/runs`, undefined, signal),
    refetchInterval: LIST_POLL_MS,
  });

/** `projectHint` names the project of a pipeline GitLab started by itself (TMS has no record of it). */
const runQuery = (projectHint?: string, against?: string) => ({
  ...(projectHint ? { project: projectHint } : {}),
  ...(against ? { against } : {}),
});

export const useRun = (runId: string, projectHint?: string) =>
  useQuery({
    queryKey: queryKeys.automation.run(pipelineIdOf(runId)),
    queryFn: ({ signal }) =>
      api.get<AutomationRunDto>(`${A}/runs/${pipelineIdOf(runId)}`, runQuery(projectHint), signal),
    refetchInterval: (query) =>
      query.state.data &&
      (query.state.data.status === 'running' || query.state.data.status === 'pending')
        ? RUN_POLL_MS
        : false,
  });

export const useRunResults = (
  runId: string,
  {
    projectHint,
    against,
    enabled = true,
  }: { projectHint?: string; against?: string; enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: queryKeys.automation.runPart(pipelineIdOf(runId), 'results', against),
    queryFn: ({ signal }) =>
      api.get<AutomationRunResultsDto>(
        `${A}/runs/${pipelineIdOf(runId)}/results`,
        runQuery(projectHint, against),
        signal,
      ),
    enabled,
    // GitLab builds the report a little after the pipeline ends
    refetchInterval: (query) => (query.state.data && !query.state.data.ready ? RUN_POLL_MS : false),
  });

export const useRunCauses = (runId: string | undefined, projectHint?: string) =>
  useQuery({
    queryKey: queryKeys.automation.runPart(pipelineIdOf(runId ?? ''), 'causes'),
    queryFn: ({ signal }) =>
      api.get<AutomationRunCausesDto>(
        `${A}/runs/${pipelineIdOf(runId ?? '')}/causes`,
        runQuery(projectHint),
        signal,
      ),
    enabled: Boolean(runId),
  });

/** The run report for people outside the code, as an Excel file (the API builds it). */
export const runReportHref = (runId: string, projectId: string): string =>
  `/api/v1${A}/runs/${pipelineIdOf(runId)}/report.xlsx?project=${enc(projectId)}`;

/** Where a report link of a run list leads: the API finds the job and redirects to GitLab. */
export const reportHref = (
  runId: string,
  kind: 'allure' | 'gradle',
  projectHint?: string,
): string =>
  `/api/v1${A}/runs/${pipelineIdOf(runId)}/report/${kind}${projectHint ? `?project=${enc(projectHint)}` : ''}`;

/**
 * Starts a run, then tells TestRun which pool accounts it took (TestRun keeps
 * the pool). The run has started even if TestRun could not be told — the
 * result says so, and the page shows it instead of failing.
 */
export const useStartRun = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      config: AutomationRunConfig,
    ): Promise<{ run: AutomationStartedRunDto; marksError: string | null }> => {
      const run = await api.post<AutomationStartedRunDto>(
        `${A}/projects/${enc(projectId)}/runs`,
        config,
      );
      const marks = {
        spendAccounts: config.spendAccounts ?? [],
        issueRegistration: config.issueRegistration ?? [],
        usedAccounts: config.usedAccounts ?? [],
      };
      let marksError: string | null = null;
      if (
        marks.spendAccounts.length ||
        marks.issueRegistration.length ||
        marks.usedAccounts.length
      ) {
        try {
          await automationRequest(queryClient, '/runs/marks', {
            method: 'POST',
            body: {
              projectId,
              gitlabProjectId: run.gitlabProjectId,
              runId: run.pipelineId,
              ...marks,
            },
          });
        } catch (error) {
          marksError = error instanceof Error ? error.message : String(error);
        }
      }
      return { run, marksError };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.automation.all });
    },
  });
};

export const useCancelRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      api.post<{ ok: true }>(`${A}/runs/${pipelineIdOf(runId)}/cancel`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.automation.all }),
  });
};

export const useRerunFailed = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, classes }: { runId: string; classes?: string[] }) =>
      api.post<AutomationRunDto>(
        `${A}/runs/${pipelineIdOf(runId)}/rerun-failed`,
        classes?.length ? { classes } : {},
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.automation.all }),
  });
};

/* ─── full runs ─── */

export const useFullRuns = () =>
  useQuery({
    queryKey: queryKeys.automation.fullRuns,
    queryFn: ({ signal }) =>
      api.get<AutomationFullRunSummaryDto[]>(`${A}/fullruns`, undefined, signal),
  });

export const useFullRun = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.automation.fullRun(id ?? ''),
    queryFn: ({ signal }) =>
      api.get<AutomationFullRunDto>(`${A}/fullruns/${enc(id ?? '')}`, undefined, signal),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data?.projects.some((p) => p.status === 'running' || p.status === 'pending')
        ? LIST_POLL_MS
        : false,
  });

export const useStartFullRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { environment?: string; projectIds?: string[] }) =>
      api.post<AutomationStartedFullRunDto>(`${A}/fullruns`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.automation.all }),
  });
};

export const useKnownDefects = () =>
  useQuery({
    queryKey: queryKeys.automation.knownDefects,
    queryFn: ({ signal }) =>
      api.get<AutomationKnownDefectDto[]>(`${A}/known-defects`, undefined, signal),
    staleTime: Infinity,
  });

/* ─── TestRun: the account pool ─── */

const testRun = <T>(queryClient: QueryClient, path: string, signal?: AbortSignal) =>
  automationRequest<T>(queryClient, path, signal ? { signal } : {});

export const usePoolAccounts = (enabled = true) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.automation.testRun('pool'),
    queryFn: ({ signal }) => testRun<PoolList>(queryClient, '/accounts', signal),
    enabled,
    // «check all» runs in the background on TestRun
    refetchInterval: (query) => (query.state.data?.checkAll.running ? 4_000 : false),
  });
};

export const usePoolAccount = (accountId: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.automation.testRun('pool', accountId),
    queryFn: ({ signal }) =>
      testRun<PoolAccount>(queryClient, `/accounts/${enc(accountId)}`, signal),
    refetchInterval: (query) => (query.state.data?.repair?.status === 'running' ? 4_000 : false),
  });
};

export const useRegistrationAccounts = (enabled = true) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.automation.testRun('registration'),
    queryFn: ({ signal }) =>
      testRun<RegistrationList>(queryClient, '/registration-accounts', signal),
    enabled,
  });
};

export const useAccountChanges = (days = 1, poolId?: string) => {
  const queryClient = useQueryClient();
  const query = new URLSearchParams({ days: String(days), ...(poolId ? { poolId } : {}) });
  return useQuery({
    queryKey: queryKeys.automation.testRun('changes', String(days), poolId ?? ''),
    queryFn: ({ signal }) =>
      testRun<AccountChanges>(queryClient, `/accounts/changes?${query}`, signal),
  });
};

/**
 * «Ready for tests»: the project's classes and what they require × the pool's
 * accounts. The plan is in TMS and the pool in TestRun, so the browser reads the
 * plan here and asks TestRun to match it. Null — the project was never scanned.
 */
export const useReadiness = (projectId: string, enabled = true) => {
  const queryClient = useQueryClient();
  const plan = useTestPlan(projectId, enabled);
  return useQuery({
    queryKey: queryKeys.automation.testRun('readiness', projectId),
    queryFn: ({ signal }) =>
      plan.data
        ? automationRequest<ReadinessMatrix>(queryClient, '/readiness', {
            method: 'POST',
            body: { plan: plan.data },
            signal,
          })
        : null,
    enabled: enabled && plan.isSuccess,
    refetchInterval: (query) =>
      query.state.data?.accounts.some((a) => a.repair?.status === 'running') ? 4_000 : false,
  });
};

const invalidateTestRun = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: queryKeys.automation.testRun() });

/** Re-checks a pool account (logs in on the stand). probe: false — read only, no 1 ₸ QR probe. */
export const useRecheckAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, probe }: { id: string; probe?: boolean }) =>
      automationRequest<PoolAccountSummary>(queryClient, `/accounts/${enc(id)}/check`, {
        method: 'POST',
        body: probe === false ? { probe: false } : {},
      }),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

/**
 * Repairs a capability of a pool account. «Salary to employees» must leave the
 * employees tests rely on alone; their list is in the TMS plans, so it goes along.
 */
export const useRepairAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, key }: { id: string; key: string }) => {
      const testEmployees =
        key === 'salaryEmployees'
          ? await api.get<AutomationTestEmployeesDto>(`${A}/test-employees`)
          : undefined;
      return automationRequest<PoolAccountSummary>(queryClient, `/accounts/${enc(id)}/repair`, {
        method: 'POST',
        body: { key, ...(testEmployees ? { testEmployees } : {}) },
      });
    },
    onSuccess: () => invalidateTestRun(queryClient),
  });
};

/** «Back in use»: the class may get this account again (or every class, without one). */
export const useUnspendAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, className }: { id: string; className?: string }) =>
      automationRequest<PoolAccountSummary>(queryClient, `/accounts/${enc(id)}/unspend`, {
        method: 'POST',
        body: className ? { class: className } : {},
      }),
    onSuccess: () => invalidateTestRun(queryClient),
  });
};
